import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Loader2, RefreshCw } from "lucide-react";
import { LocationOption } from "@/services/copyVariationsService";

interface CopyVariationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (sourceLocationId: string) => void;
  locationOptions: LocationOption[];
  destinationLocationName: string;
  isLoading: boolean;
}

export const CopyVariationsModal: React.FC<CopyVariationsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  locationOptions,
  destinationLocationName,
  isLoading
}) => {
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");

  const selectedLocation = locationOptions.find(loc => loc.id === selectedLocationId);

  const handleConfirm = () => {
    if (selectedLocationId) {
      onConfirm(selectedLocationId);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedLocationId("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy programs from location
          </DialogTitle>
          <DialogDescription>
            Select a location to copy all programs/variations from into "{destinationLocationName}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-900 block mb-2">
              Choose source location:
            </label>
            <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a location to copy from..." />
              </SelectTrigger>
              <SelectContent>
                {locationOptions.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{location.name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({location.variationsCount} program{location.variationsCount !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLocation && (
            <Alert>
              <RefreshCw className="h-4 w-4" />
              <AlertDescription>
                This will copy <strong>{selectedLocation.variationsCount} program{selectedLocation.variationsCount !== 1 ? 's' : ''}</strong> from 
                "{selectedLocation.name}" and add them to the current location. 
                Existing programs in "{destinationLocationName}" will be preserved.
                <br /><br />
                <strong>Note:</strong> The page will refresh after copying to show the updated programs list.
              </AlertDescription>
            </Alert>
          )}

          {locationOptions.length === 0 && (
            <Alert>
              <AlertDescription>
                No other locations found in this organization to copy from.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={isLoading || !selectedLocationId}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Copying...
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy now
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 